import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { fetchRecipesFromShopify } from '../redux/actions/recipeActions';
import RecipeCard from '../components/recipes/RecipeCard';
import { Recipe } from '../types/shopify';
import LoadingSpinner from '../components/common/LoadingSpinner';

interface RecipesProps {
  fetchRecipesFromShopify: (blogHandle?: string, cursor?: string) => Promise<Recipe[]>;
  shopifyRecipes: Recipe[];
  isLoading: boolean;
  error: string | null;
  paginationInfo: {
    hasNextPage: boolean;
    endCursor: string | null;
  };
}

const Recipes: React.FC<RecipesProps> = ({
  fetchRecipesFromShopify,
  shopifyRecipes,
  isLoading,
  error,
  paginationInfo
}) => {
  const [blogHandle] = useState('recipes');
  
  useEffect(() => {
    // Fetch recipes when component mounts
    fetchRecipesFromShopify(blogHandle);
  }, [fetchRecipesFromShopify, blogHandle]);
  
  const loadMoreRecipes = () => {
    if (paginationInfo?.hasNextPage && paginationInfo?.endCursor) {
      fetchRecipesFromShopify(blogHandle, paginationInfo.endCursor);
    }
  };
  
  return (
    <div className="u-background--lightgrey">
      <div className="container">
        <div className="u-pt--32 u-pb--16">
          <h1 className="u-main-font--xlarge u-bold">Recipes</h1>
          <p className="u-main-font--medium">Discover delicious recipes to make with your groceries</p>
        </div>
        
        {error && (
          <div className="u-background--red u-color-white u-p--16 u-br--small u-mb--16">
            <p className="u-main-font--medium">{error}</p>
          </div>
        )}
        
        {isLoading && shopifyRecipes.length === 0 ? (
          <div className="u-flex u-justify-center u-p--32">
            <LoadingSpinner />
          </div>
        ) : (
          <>
            <div className="u-flex u-flex-wrap u-gap--16 u-pb--32">
              {shopifyRecipes.map(recipe => (
                <RecipeCard 
                  key={recipe.id} 
                  shopifyRecipe={recipe} 
                  isShopifyRecipe={true} 
                />
              ))}
            </div>
            
            {paginationInfo?.hasNextPage && (
              <div className="u-flex u-justify-center u-pb--32">
                <button 
                  className="u-br--small u-background--magenta u-color-white u-p--12 u-main-font--medium u-bold"
                  onClick={loadMoreRecipes}
                  disabled={isLoading}
                >
                  {isLoading ? 'Loading...' : 'Load More Recipes'}
                </button>
              </div>
            )}
            
            {shopifyRecipes.length === 0 && !isLoading && (
              <div className="u-p--32 u-text-center">
                <p className="u-main-font--large">No recipes found</p>
                <p className="u-main-font--medium">Check back later for new recipes</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state: any) => ({
  shopifyRecipes: state.recipes.shopifyRecipes || [],
  isLoading: state.recipes.isLoading,
  error: state.recipes.error,
  paginationInfo: state.recipes.paginationInfo
});

const mapDispatchToProps = {
  fetchRecipesFromShopify
};

export default connect(mapStateToProps, mapDispatchToProps)(Recipes);
